import { bySiblingOrder } from "@/lib/tree";

const W = {
  U: { male: "father", female: "mother", x: "parent" },
  D: { male: "son", female: "daughter", x: "child" },
  S: { male: "husband", female: "wife", x: "spouse" },
  B: { male: "brother", female: "sister", x: "sibling" },
};
const g = (p) => (p?.gender === "male" || p?.gender === "female" ? p.gender : "x");

const HINDI = {
  father: "Pita / Papa (पिता)", mother: "Maa (माँ)", son: "Beta (बेटा)", daughter: "Beti (बेटी)",
  brother: "Bhai (भाई)", sister: "Behen (बहन)", "elder brother": "Bhaiya (भैया)", "elder sister": "Didi (दीदी)", husband: "Pati (पति)", wife: "Patni (पत्नी)",
  "father's father": "Dada (दादा)", "father's mother": "Dadi (दादी)", "mother's father": "Nana (नाना)", "mother's mother": "Nani (नानी)",
  "father's father's father": "Pardada (परदादा)", "father's father's mother": "Pardadi (परदादी)",
  "mother's father's father": "Parnana (परनाना)", "mother's father's mother": "Parnani (परनानी)",
  "father's elder brother": "Tau / Taya (ताऊ)", "father's younger brother": "Chacha (चाचा)", "father's brother": "Chacha / Tau (चाचा / ताऊ)",
  "father's elder brother's wife": "Tai (ताई)", "father's younger brother's wife": "Chachi (चाची)", "father's brother's wife": "Chachi / Tai (चाची / ताई)",
  "father's sister": "Bua (बुआ)", "father's sister's husband": "Phoopha (फूफा)",
  "mother's brother": "Mama (मामा)", "mother's brother's wife": "Mami (मामी)",
  "mother's sister": "Mausi (मौसी)", "mother's sister's husband": "Mausa (मौसा)",
  "brother's son": "Bhatija (भतीजा)", "brother's daughter": "Bhatiji (भतीजी)",
  "sister's son": "Bhanja (भांजा)", "sister's daughter": "Bhanji (भांजी)",
  "son's son": "Pota (पोता)", "son's daughter": "Poti (पोती)", "daughter's son": "Nati / Nawasa (नाती)", "daughter's daughter": "Natin / Nawasi (नातिन)",
  "son's wife": "Bahu (बहू)", "daughter's husband": "Damaad / Jamai (दामाद)",
  "brother's wife": "Bhabhi (भाभी)", "sister's husband": "Jija (जीजा)",
  "elder brother's wife": "Bhabhi (भाभी)", "younger brother's wife": "Bhabhi / Bahu (भाभी)",
  "husband's father": "Sasur (ससुर)", "wife's father": "Sasur (ससुर)", "husband's mother": "Saas (सास)", "wife's mother": "Saas (सास)",
  "wife's brother": "Saala (साला)", "wife's sister": "Saali (साली)", "husband's sister": "Nanad (ननद)",
  "husband's elder brother": "Jeth (जेठ)", "husband's younger brother": "Devar (देवर)", "husband's brother": "Devar / Jeth (देवर / जेठ)",
  "husband's elder brother's wife": "Jethani (जेठानी)", "husband's younger brother's wife": "Devrani (देवरानी)",
  "wife's sister's husband": "Saadhu (साढ़ू)", "husband's sister's husband": "Nandoi (ननदोई)",
  "father's brother's son": "Chachera Bhai (चचेरा भाई)", "father's brother's daughter": "Chacheri Behen (चचेरी बहन)",
  "father's sister's son": "Phuphera Bhai (फुफेरा भाई)", "father's sister's daughter": "Phupheri Behen (फुफेरी बहन)",
  "mother's brother's son": "Mamera Bhai (ममेरा भाई)", "mother's brother's daughter": "Mameri Behen (ममेरी बहन)",
  "mother's sister's son": "Mausera Bhai (मौसेरा भाई)", "mother's sister's daughter": "Mauseri Behen (मौसेरी बहन)",
  "father's father's brother": "Dada (दादा)", "father's father's brother's son": "Chacha / Tau (चाचा / ताऊ)",
};

function shortestPath(aId, bId, t) {
  const dist = { [aId]: 0 };
  const prev = {};
  const queue = [aId];
  while (queue.length) {
    queue.sort((x, y) => dist[x] - dist[y]);
    const cur = queue.shift();
    if (cur === bId) break;
    const p = t.byId[cur];
    const edges = [
      ...p.parent_ids.map((n) => [n, "U", 1]),
      ...t.childrenOf(cur).map((c) => [c.person_id, "D", 1]),
      ...p.spouse_ids.map((n) => [n, "S", 1.05]),
    ];
    edges.forEach(([n, step, w]) => {
      if (!t.byId[n]) return;
      const nd = dist[cur] + w;
      if (dist[n] === undefined || nd < dist[n]) { dist[n] = nd; prev[n] = [cur, step]; queue.push(n); }
    });
  }
  if (dist[bId] === undefined) return null;
  const steps = [];
  for (let n = bId; n !== aId; n = prev[n][0]) steps.unshift({ step: prev[n][1], to: n, from: prev[n][0] });
  return steps;
}

function compress(steps) {
  const out = [];
  for (let i = 0; i < steps.length; i++) {
    const s = steps[i];
    const nx = steps[i + 1];
    if (s.step === "U" && nx?.step === "D" && nx.to !== s.from) {
      out.push({ step: "B", to: nx.to, from: s.from });
      i++;
    } else out.push(s);
  }
  return out;
}

const age = (t, from, to) => {
  const cmp = bySiblingOrder(t.byId[to], t.byId[from]);
  return cmp < 0 ? "elder " : cmp > 0 ? "younger " : "";
};

function englishTerm(c, last) {
  const kinds = c.map((s) => s.step).join("");
  const m = /^(U*)(B?)(D*)$/.exec(kinds);
  if (!m) return null;
  const up = m[1].length + (m[2] ? 1 : 0);
  const down = m[3].length + (m[2] ? 1 : 0);
  const gx = g(last);
  const great = (n) => "great-".repeat(Math.max(0, n));
  if (up === 2 && down === 0) return gx === "male" ? "grandfather" : gx === "female" ? "grandmother" : "grandparent";
  if (up > 2 && down === 0) return `${great(up - 2)}grand${gx === "male" ? "father" : gx === "female" ? "mother" : "parent"}`;
  if (down >= 2 && up === 0) return `${great(down - 2)}grand${gx === "male" ? "son" : gx === "female" ? "daughter" : "child"}`;
  if (down === 1 && up >= 2) return `${great(up - 2)}${gx === "male" ? "uncle" : gx === "female" ? "aunt" : "uncle / aunt"}`;
  if (up === 1 && down >= 2) return `${great(down - 2)}${gx === "male" ? "nephew" : gx === "female" ? "niece" : "nephew / niece"}`;
  if (up >= 2 && down >= 2) {
    const deg = Math.min(up, down) - 1;
    const removed = Math.abs(up - down);
    const ord = ["", "first", "second", "third", "fourth", "fifth"][deg] || `${deg}th`;
    return `${ord} cousin${removed ? `, ${removed === 1 ? "once" : removed === 2 ? "twice" : `${removed} times`} removed` : ""}`;
  }
  return null;
}

export function findRelation(aId, bId, t) {
  if (aId === bId) return { same: true };
  const raw = shortestPath(aId, bId, t);
  if (!raw) return null;
  const c = compress(raw);
  const words = c.map((s) => W[s.step][g(t.byId[s.to])]);
  const chain = words.join("'s ");
  const agedChain = c.map((s, i) => {
    if (s.step !== "B") return words[i];
    const sib = i === 0 ? aId : c[i - 1].to;
    return age(t, sib, s.to) + words[i];
  }).join("'s ");
  const hindi = HINDI[agedChain] || HINDI[chain] || null;
  const last = t.byId[bId];
  return { chain: agedChain || chain, hindi, english: englishTerm(c, last), path: [aId, ...raw.map((s) => s.to)] };
}
